import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';

const BASE_ROLE_OPTIONS = [
  { code: 'role-acct-mgmt', label: 'Account Management' },
  { code: 'role-key-mgmt', label: 'Key Management' },
];

const STATUS_OPTIONS = ['All Statuses', 'Pending', 'Accepted', 'Rejected', 'Expired'];

const InviteHistory = ({ isOrg, canManageAccount, canAssignSupport }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const roleOptions = canAssignSupport
    ? [...BASE_ROLE_OPTIONS, { code: 'role-support', label: 'Support' }]
    : BASE_ROLE_OPTIONS;

  const fetchInvites = async () => {
    setLoading(true);
    try {
      const response = await api.get('/acct-mgmt/v1/account/invites');
      setInvites(response.data || []);
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load invite history.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOrg || !canManageAccount) return;
    fetchInvites();
  }, [isOrg, canManageAccount]);

  const filteredInvites = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return invites.filter((invite) => {
      const statusMatch = statusFilter === 'All Statuses' || invite.status === statusFilter;
      if (!statusMatch) return false;
      if (!normalizedSearch) return true;
      const roles = (invite.roles || [])
        .map((role) => roleOptions.find((r) => r.code === role)?.label || role)
        .join(' ')
        .toLowerCase();
      const haystack = `${invite.email} ${invite.status} ${roles}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [invites, searchTerm, statusFilter]);

  const exportCsv = () => {
    const headers = ['Email', 'Roles', 'Status', 'Created', 'Expires'];
    const rows = filteredInvites.map((invite) => {
      const roles = (invite.roles || [])
        .map((role) => roleOptions.find((r) => r.code === role)?.label || role)
        .join(' | ');
      return [
        `"${(invite.email || '').replace(/"/g, '""')}"`,
        `"${roles.replace(/"/g, '""')}"`,
        `"${(invite.status || '').replace(/"/g, '""')}"`,
        invite.created_at ? new Date(invite.created_at).toLocaleDateString() : '',
        invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : '',
      ].join(',');
    });
    const csvLines = [headers.join(','), ...rows];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'invite-history.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (!isOrg) {
    return (
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Invite History</h5>
          <p className="text-muted mb-0">Individual accounts cannot invite users.</p>
        </div>
      </div>
    );
  }
  if (!canManageAccount) {
    return (
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Invite History</h5>
          <p className="text-muted mb-0">You do not have permission to view invite history.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2">
        <Link className="btn btn-link p-0" to="/account/invites">
          &lt; Back to Invites
        </Link>
      </div>
      <div className="card">
        <div className="card-body">
        <div className="card-title d-flex flex-wrap align-items-center justify-content-between w-100 mb-3">
          <h5 className="mb-0">Invite History</h5>
          <button className="btn btn-outline-primary btn-sm" type="button" onClick={exportCsv}>
            <i className="fas fa-file-csv me-2"></i>
            Export CSV
          </button>
        </div>
        {status.message && (
          <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>
        )}
        <div className="row g-3 align-items-end mb-3">
          <div className="col-md-7">
            <label className="form-label">Search</label>
            <input
              className="form-control"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search email, role, or status"
            />
          </div>
          <div className="col-md-5">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
        {loading ? (
          <div className="text-muted">Loading invite history...</div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvites.map((invite) => (
                  <tr key={invite.id}>
                    <td>{invite.email}</td>
                    <td>
                      {(invite.roles || []).length === 0 ? (
                        <span className="text-muted">None</span>
                      ) : (
                        (invite.roles || []).map((role) => (
                          <span key={role} className="badge bg-light text-dark border me-1">
                            {roleOptions.find((r) => r.code === role)?.label || role}
                          </span>
                        ))
                      )}
                    </td>
                    <td>
                      <span className={`badge ${
                        invite.status === 'Pending'
                          ? 'bg-warning text-dark'
                          : invite.status === 'Accepted'
                            ? 'bg-success'
                            : invite.status === 'Rejected'
                              ? 'bg-secondary'
                              : 'bg-light text-dark'
                      }`}>
                        {invite.status}
                      </span>
                    </td>
                    <td>{invite.created_at ? new Date(invite.created_at).toLocaleDateString() : '—'}</td>
                    <td>{new Date(invite.expires_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {filteredInvites.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-muted text-center">No invites match your filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default InviteHistory;
